/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core';
import mbpLogger from '@module-logger/mbp';
import { useDispatch, useSelector } from 'react-redux';
import {
    array, bool, func, object,
} from 'prop-types';

import GraphqlProductListAdCategoryContainer from '../GraphqlProductListAddCategroy/GraphqlProductListAdCategoryContainer';
import { getBrand } from '../../../../../pages/Account/state/ducks/Common/Common-Selectors';
import useSalesforcePersonalizedContentQuery from '../../../../../helpers/Personalization/useSalesforcePersonalizedContentQuery';
import noop from '../../../../../helpers/noop';
import useClickStreamCustomEventsTracking from '../../../../../helpers/Personalization/useClickstreamCustomEventsTracking';
import { getPresentationFamily } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import RecommendationsBuilder from '../StandardProductsPage/RecommendationsBuilder';
import { setIsPLASalesforceABTest } from '../../../../../../state/ducks/App/App-Actions';
import RenderWhenVisible from '../../../../../helpers/componentVisibility/RenderWhenVisible';
import { trackEvent } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    contentBottom: {
        width: '100%',
        margin: '0 auto 60px auto',
        maxWidth: '1380px',
        // Tablet
        [theme.breakpoints.down('md')]: {
            padding: '0 20px 0',
        },
        // Mobile
        [theme.breakpoints.down('sm')]: {
            margin: '0 0 15px 0',
            padding: '0',
        },
    },
    contentBottomDivider: {
        margin: '0 0 15px 0',
        textAlign: 'center',
        width: '100%',
        '&--title': {
            margin: '0',
            textAlign: 'center',
            fontSize: '16px',
            fontWeight: '600',
        },
    },
    plaContentBottom: {
        '@media screen and (min-width:600px)': {
            marginBottom: '0px',
        },
        '& $contentBottomDivider': {
            margin: '28px 0px 16px',
            '@media screen and (min-width:600px)': {
                margin: '32px 0px 28px',
            },
            '&--title': {
                fontSize: '28px',
                lineHeight: '36px',
                textTransform: 'lowercase',
                padding: '0px 0px 1px 16px !important',
                '@media screen and (max-width:1024px)': {
                    fontSize: '20px',
                    lineHeight: '30px',
                },
                '&:first-letter': {
                    textTransform: 'capitalize',
                },
            },
        },
        '& a': {
            '& span': {
                fontSize: '14px !important',
                lineHeight: '19px !important',
            },
        },
        '& .MuiGrid-container ': {
            margin: '0 auto',
        },
        '& .MuiGrid-item': {
            margin: '0px',
        },
    },
    plaFoodContentBottom: {
        '& $contentBottomDivider': {
            '&--title': {
                textAlign: 'left !important',
                fontSize: '26px',
                lineHeight: '36px',
                '@media screen and (max-width:1024px)': {
                    fontSize: '20px',
                    lineHeight: '30px',
                },
            },
        },
    },
}));

export default function PLARecsABWrapper({
    isPlaEnabled, urlParams, trackEvent: trackEventProp, product, skuData, isPlaRedesignEnabled, recommendations, featureFlags,
}) {
    const dispatch = useDispatch();

    // @intention: set default filter data for collection
    const filterData = {
        lowToHighBtn: '',
        highToLowBtn: '',
        sellingBtn: '',
        giftBox: '',
        localFlorist: '',
    };
    const plaChecks = isPlaEnabled && urlParams;
    const param = urlParams && Object.keys(urlParams).includes('?recommended_product') ? '?recommended_product' : 'recommended_product';
    const recommendedProductChecks = urlParams?.[param];
    const isSympathySku = skuData?.some((sku) => sku?.sympathyValue === '1' || sku?.sympathyValue === '2');
    const presentationFamily = useSelector(getPresentationFamily);
    const classes = useStyles();
    const brand = useSelector(getBrand);
    const customerBoughtClass = presentationFamily === 'flower' ? classes.plaContentBottom : `${classes.plaContentBottom} ${classes.plaFoodContentBottom}`;
    const renderFallback = (trackClickThrough, wrapperData) => {
        if (plaChecks || recommendedProductChecks) {
            return (
                <RenderWhenVisible>
                    <div className={`${isPlaRedesignEnabled && customerBoughtClass } ${classes.contentBottom}`}>
                        <div className={classes.contentBottomDivider}>
                            <p className={`${classes.contentBottomDivider}--title`}>Customers Also Bought</p>
                        </div>
                        <GraphqlProductListAdCategoryContainer filterData={filterData} trackEvent={trackEventProp} urlParams={urlParams} brand={brand} trackClickThrough={trackClickThrough} PLARecommendations product={product} wrapperData={wrapperData} />
                    </div>
                </RenderWhenVisible>
            );
        }
        return null;
    };

    const {
        loading, error, data: wrapperData, variables,
    } = useSalesforcePersonalizedContentQuery({
        queryName: 'PLARecsABWrapper',
        interactionName: '1800flowers - Get Campaign - PLA Wrapper',
        targeting: {
            pageType: 'product',
        },
        optInHoldoutParticipation: true,
        resolveIdentity: true,
    });

    // passed down several components for wrapper click event
    const campaignTrackClickThrough = useClickStreamCustomEventsTracking({
        salesforceResponse: wrapperData?.contentSF,
        isFireImpression: false,
        page: { type: 'product' },
    });

    const trackClickThrough = () => {
        campaignTrackClickThrough();

        dispatch(trackEvent({
            eventName: 'feature_interaction',
            eventCategory: 'Product Page',
            eventAction: 'Customers Also Bought Clicks',
            feature_detail: `${product?.partNumber} - ${brand?.id}`,
            feature_element: 'Customers Also Bought Control',
            feature_category: 'PLA',
        }));
    };

    if (loading || variables?.skip) return null;

    if (error) {
        mbpLogger.logError({
            function: 'PLARecsABWrapper.js',
            message: 'Error loading data from saleforce api',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    if (!recommendations || recommendations?.length === 0) {
        mbpLogger.logError({
            function: 'PLARecsABWrapper.js',
            message: 'No recommendations found. Moving to fallback ui',
            appName: process.env.npm_package_name,
            module: 'PersonalizedContent',
            jsError: error,
        });
    }

    const sfData = wrapperData?.contentSF || null;
    const userGroup = sfData?.campaign?.campaignResponses?.[0]?.payload?.userGroup || 'control';
    const isNotControl = userGroup.toLowerCase() !== 'control';
    const isPlaSFRecsEnabled = featureFlags['is-pla-sf-recs-enabled'];

    // if LD ff is enabled, render control or variant and emit events
    if (isPlaSFRecsEnabled) {
        if (isNotControl) {
            if (recommendations?.length > 0) {
                dispatch(setIsPLASalesforceABTest(true));
                return (
                    <RenderWhenVisible>
                        <RecommendationsBuilder
                            featureFlags={featureFlags}
                            product={product}
                            recommendations={recommendations}
                            sortedData={skuData}
                            isPlaRedesignEnabled={isPlaRedesignEnabled}
                            isFromSFQueryWrapper
                            wrapperData={wrapperData}
                            campaignTrackClickThrough={campaignTrackClickThrough}
                        />
                    </RenderWhenVisible>
                );
            }

            return null;
        }

        dispatch(setIsPLASalesforceABTest(false));
        return renderFallback(trackClickThrough, wrapperData);
    }

    // if LD ff is disabled, per brand, render static fallback with no events
    return renderFallback();
}

PLARecsABWrapper.propTypes = {
    isPlaEnabled: bool,
    urlParams: object,
    trackEvent: func,
    product: object.isRequired,
    skuData: array.isRequired,
    isPlaRedesignEnabled: bool.isRequired,
    recommendations: array,
    featureFlags: object,
};

PLARecsABWrapper.defaultProps = {
    isPlaEnabled: false,
    urlParams: {},
    trackEvent: noop,
    recommendations: [],
    featureFlags: {},
};
